import { useEffect, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { getStatusBadgeConfig } from "@/lib/utils/appointment-status";
import { Badge } from "@/components/ui/badge";
import { EventTooltip } from "./EventTooltip";
import type { CustomEventProps } from "./CalendarEventTypes";
import { getEventPetsLabel, getStatusColorValue } from "./calendarEventUtils";
import { getInitials } from "@/lib/utils/string-utils";
import { renderIconBySlug } from "@/lib/event-icons";
import { AppointmentCardSummaryLines } from "./AppointmentCardSummaryLines";
import { AppointmentDriverBadge } from "@/components/calendar/AppointmentDriverBadge";

export function WeekEvent({ event, statuses, showAppointmentItemsOnCards }: CustomEventProps) {
  const { t } = useTranslation("calendar");
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const ownerName: string | undefined = event.owner?.name;
  const statusLabel = getStatusBadgeConfig(event.status, statuses).label;
  const statusColor = getStatusColorValue(event.status, statuses);

  useEffect(() => {
    const triggerElement = triggerRef.current;
    if (!triggerElement) return;

    const eventContainer = triggerElement.closest(".rbc-addons-dnd-resizable");
    if (!eventContainer) return;

    const openTooltip = () => {
      triggerElement.dispatchEvent(new PointerEvent("pointerenter", { bubbles: true }));
      triggerElement.dispatchEvent(new MouseEvent("mouseenter", { bubbles: true }));
    };

    const closeTooltip = () => {
      triggerElement.dispatchEvent(new PointerEvent("pointerleave", { bubbles: true }));
      triggerElement.dispatchEvent(new MouseEvent("mouseleave", { bubbles: true }));
    };

    const handles = [
      eventContainer.querySelector(".rbc-addons-dnd-resize-ew-anchor:last-child"),
      eventContainer.querySelector(".rbc-addons-dnd-resize-ns-anchor:last-child"),
    ].filter((handle): handle is HTMLElement => handle instanceof HTMLElement);

    if (handles.length === 0) return;

    handles.forEach((handle) => {
      handle.addEventListener("mouseenter", openTooltip);
      handle.addEventListener("mouseleave", closeTooltip);
    });

    return () => {
      handles.forEach((handle) => {
        handle.removeEventListener("mouseenter", openTooltip);
        handle.removeEventListener("mouseleave", closeTooltip);
      });
    };
  }, []);


  return (
    <EventTooltip event={event} statuses={statuses} triggerRef={triggerRef}>
      <div
        ref={triggerRef}
        className={cn(
          "flex h-[calc(100%-12px)] min-h-0 flex-col gap-1 overflow-hidden",
        )}
      >
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1 flex-wrap">
            <p className="text-[11px] font-semibold leading-tight truncate">{getEventPetsLabel(event)}</p>
            {event.financial_status === "pago" ? (
              <Badge className="h-4 px-1 text-[9px] font-medium bg-emerald-700 text-white border-transparent">
                {t("financialSettled")}
              </Badge>
            ) : null}
            <AppointmentDriverBadge
              isTaxiDog={event.is_taxi_dog}
              driverType={event.taxi_dog_type}
              iconClassName="h-2.5 w-2.5"
            />
          </div>
          {ownerName ? (
            <p className="mt-0.5 hidden text-[10px] leading-tight opacity-80 truncate sm:block">
              {ownerName}
            </p>
          ) : null}
          <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] leading-tight truncate" style={{ color: statusColor }}>
            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusColor }} /> {statusLabel}
          </span>
          {showAppointmentItemsOnCards ? (
            <AppointmentCardSummaryLines items={event.summary_items ?? []} maxItems={2} className="mt-1 space-y-0.5" />
          ) : null}
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          {event.vet?.full_name ? (
            <Avatar className="h-6 w-6">
              {event.vet.avatar_url ? (
                <AvatarImage src={event.vet.avatar_url} alt={event.vet.full_name ?? t("professionalAlt")} />
              ) : null}
              <AvatarFallback className="text-[9px] bg-white border border-slate-200">{getInitials(event.vet.full_name)}</AvatarFallback>
            </Avatar>
          ) : (
            <span className="h-6 w-6" />
          )}

          <div
            className="h-5 w-5 rounded flex items-center justify-center shrink-0"
            style={{ backgroundColor: event.clinic_appointment_type?.color ?? "#6366f1" }}
          >
            {renderIconBySlug(event.clinic_appointment_type?.icon ?? "clipboard-plus", { tone: "white", sizeClass: "h-3.5 w-3.5" })}
          </div>
        </div>
      </div>
    </EventTooltip>
  );
}
